/**
 * UCM 未知板号「快速建档」弹窗
 * 在不离开 UCM 详情页的前提下，用报文里的板号 + 预填位置快速建一条 ULD 台账。
 * 复用现有 ULD 建档接口（POST /v1/uld/master，单个模式），字段与「ULD 管理」单条建档对齐
 * （制造/物理属性在 UldForm 中已隐藏，这里同样不收）。板号、入库机场可人工改。
 */

import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, message } from 'antd';
import dayjs from 'dayjs';
import { createUld } from '../../../../api/uldApi';
import { toCreateUldInitialStatusFields } from '../../lib/createUldInitialStatus';
import { NEW_ULD_CORE_STATUS_OPTIONS } from '../../UldManagement/constants/uldConstantsV3';

// 板号校验与后端建档（UldMasterCreateRequest）口径一致：类型(3位)+序列号(4-5位数字)+所有者代码(2-3位)，总长 10 位。
// 比共享的 ULD_IDENTIFIER_REGEX（任意 10 位字母数字）更严，避免"前端通过、后端报错"的割裂。
const CREATE_IDENTIFIER_REGEX = /^[A-Z0-9]{3}[0-9]{4,5}[A-Z0-9]{2,3}$/;
const CREATE_IDENTIFIER_HINT = '格式：类型(3位)+序列号(4-5位数字)+所有者代码(2-3位)，总长度10位，如 AKE12345CA';

const UcmQuickCreateUldModal = ({ visible, defaultIdentifier, defaultAirport, onClose, onCreated }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [ownershipType, setOwnershipType] = useState('自有');

    useEffect(() => {
        if (!visible) return;
        form.resetFields();
        setOwnershipType('自有');
        form.setFieldsValue({
            uld_identifier: defaultIdentifier || '',
            airport_iata: defaultAirport || '',
            initial_core_status: 'IN_STOCK',
            ownership_type: '自有',
        });
    }, [visible, defaultIdentifier, defaultAirport, form]);

    const handleOwnershipTypeChange = (value) => {
        setOwnershipType(value);
        if (value !== '租赁') {
            form.setFieldsValue({ lease_start_date: undefined, lease_end_date: undefined });
        }
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            const payload = {
                mode: 'single',
                uld_identifier: values.uld_identifier.trim().toUpperCase(),
                airport_iata: (values.airport_iata || '').trim().toUpperCase(),
                owner: values.owner.trim(),
                ownership_type: values.ownership_type,
                ...toCreateUldInitialStatusFields(values.initial_core_status),
            };
            // 可选字段：有值才带，避免空串覆盖后端默认
            const holder = (values.holder || '').trim();
            if (holder) payload.holder = holder;
            const gha = (values.gha || '').trim();
            if (gha) payload.gha = gha;
            const remarks = (values.remarks || '').trim();
            if (remarks) payload.remarks = remarks;
            // 租赁：后端要求 lease_start_date 必填，结束日期可选
            if (values.ownership_type === '租赁') {
                payload.lease_start_date = dayjs(values.lease_start_date).format('YYYY-MM-DDTHH:mm:ss');
                if (values.lease_end_date) {
                    payload.lease_end_date = dayjs(values.lease_end_date).format('YYYY-MM-DDTHH:mm:ss');
                }
            }
            await createUld(payload);
            message.success('建档成功');
            onCreated && (await onCreated(payload.uld_identifier));
            onClose && onClose();
        } catch (error) {
            message.error(error.response?.data?.detail || '建档失败');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title="快速建档（未知板号）"
            open={visible}
            onCancel={onClose}
            onOk={handleSubmit}
            okText="建档"
            confirmLoading={saving}
            destroyOnClose
            width={640}
        >
            <Form form={form} layout="vertical" name="ucmQuickCreateUld">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="uld_identifier"
                            label="板号"
                            rules={[
                                { required: true, message: '请输入板号' },
                                {
                                    validator: (_, v) => {
                                        const val = (v || '').trim().toUpperCase();
                                        if (!val) return Promise.reject(new Error('请输入板号'));
                                        if (val.length !== 10 || !CREATE_IDENTIFIER_REGEX.test(val)) {
                                            return Promise.reject(new Error(CREATE_IDENTIFIER_HINT));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input placeholder="例如: AKE12345CA" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="airport_iata"
                            label="入库机场"
                            rules={[{ required: true, message: '请输入机场三字码' }]}
                        >
                            <Input placeholder="例如: PEK" maxLength={3} style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="initial_core_status"
                            label="初始核心状态"
                            rules={[{ required: true, message: '请选择初始核心状态' }]}
                        >
                            <Select options={NEW_ULD_CORE_STATUS_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ownership_type"
                            label="所有权类型"
                            rules={[{ required: true, message: '请选择所有权类型' }]}
                        >
                            <Select
                                onChange={handleOwnershipTypeChange}
                                options={[
                                    { value: '自有', label: '自有' },
                                    { value: '租赁', label: '租赁' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="owner"
                            label="所有者（法律拥有者）"
                            rules={[{ required: true, message: '请输入所有者' }]}
                        >
                            <Input placeholder="例如: 中国国际航空股份有限公司" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="holder" label="持有者（当前实际占有者）">
                            <Input placeholder="例如: 中国东方航空股份有限公司" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="gha" label="地面处理代理">
                            <Input placeholder="例如: 首都机场地面服务有限公司" />
                        </Form.Item>
                    </Col>
                </Row>
                {ownershipType === '租赁' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="lease_start_date"
                                label="租赁开始日期"
                                rules={[{ required: true, message: '租赁类型必须填写租赁开始日期' }]}
                            >
                                <DatePicker placeholder="选择租赁开始日期" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lease_end_date"
                                label="租赁结束日期"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            const start = form.getFieldValue('lease_start_date');
                                            if (value && start && !value.isAfter(start)) {
                                                return Promise.reject(new Error('租赁结束日期必须晚于租赁开始日期'));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <DatePicker placeholder="选择租赁结束日期（可选）" style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                <Form.Item name="remarks" label="备注">
                    <Input.TextArea rows={2} placeholder="输入备注信息（可选）" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UcmQuickCreateUldModal;
